import { makeStack, type ItemStack } from "../world/items";
import { findRecipe, type Recipe } from "../world/recipes";
import { clickStack } from "./inventory";

/**
 * A detached crafting grid: 2x2 in the player's pack, 3x3 at a crafting table.
 * The grid holds real stacks; every craft consumes one item from each occupied
 * cell, exactly like the recipe matcher expects.
 */
export class CraftingGrid {
  readonly width: number;
  readonly height: number;
  readonly cells: (ItemStack | null)[];

  constructor(width: 2 | 3) {
    this.width = width;
    this.height = width;
    this.cells = new Array(width * width).fill(null);
  }

  /** The recipe the current layout matches, if any. */
  recipe(): Recipe | null {
    return findRecipe({ width: this.width, cells: this.cells });
  }

  /** What the result slot should show, without consuming anything. */
  result(): ItemStack | null {
    const recipe = this.recipe();
    return recipe ? makeStack(recipe.result.id, recipe.result.count) : null;
  }

  /** Same click rules as an inventory slot. Returns the new cursor stack. */
  clickCell(
    index: number,
    button: "left" | "right",
    held: ItemStack | null,
  ): ItemStack | null {
    return clickStack(this.cells, index, button, held);
  }

  /** Consume one ingredient per occupied cell and return the crafted stack. */
  takeResult(): ItemStack | null {
    const recipe = this.recipe();
    if (!recipe) return null;

    for (let i = 0; i < this.cells.length; i++) {
      const cell = this.cells[i];
      if (!cell) continue;
      this.cells[i] = cell.count > 1 ? { ...cell, count: cell.count - 1 } : null;
    }
    return makeStack(recipe.result.id, recipe.result.count);
  }

  /** Remove and return every cell stack (used when the screen closes). */
  drain(): ItemStack[] {
    const out: ItemStack[] = [];
    for (let i = 0; i < this.cells.length; i++) {
      const cell = this.cells[i];
      if (!cell) continue;
      out.push(cell);
      this.cells[i] = null;
    }
    return out;
  }

  clear(): void {
    this.cells.fill(null);
  }
}
